import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Configure axios base URL for production
if (process.env.NODE_ENV === 'production') {
  axios.defaults.baseURL = '';
}

function Inventory({ authData }) {
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [sortField, setSortField] = useState('last_updated');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchInventoryData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/inventory', {
        params: {
          page: currentPage,
          limit: 50,
          search: searchQuery
        },
        headers: {
          phone: authData.phone,
          otp: authData.otp
        }
      });

      setInventoryData(response.data.data);
      setTotalItems(response.data.total);
      setTotalPages(Math.ceil(response.data.total / 50));
    } catch (err) {
      console.error('Inventory fetch error:', err);
      setError('Failed to fetch inventory data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryData();
  }, [currentPage, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchInventoryData();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatArray = (array) => {
    if (!array || !Array.isArray(array)) return '-';
    return array.join(', ');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const formatQuantity = (quantity) => {
    if (!quantity) return '0';
    return parseFloat(quantity).toFixed(2);
  };

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1>Inventory Management</h1>
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by Product ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>
      </div>

      {loading && <div className="loading">Loading inventory data...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="inventory-stats">
            <p>Total Items: {totalItems} | Page {currentPage} of {totalPages}</p>
          </div>

          <div className="table-container">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('product_id')}>Product ID</th>
                  <th onClick={() => handleSort('name')}>Product Name</th>
                  <th onClick={() => handleSort('manufacturers')}>Manufacturer</th>
                  <th onClick={() => handleSort('batch_number')}>Batch Number</th>
                  <th onClick={() => handleSort('expiry_date')}>Expiry Date</th>
                  <th onClick={() => handleSort('quantity_available')}>Quantity</th>
                  <th onClick={() => handleSort('status')}>Status</th>
                  <th onClick={() => handleSort('rack_location')}>Rack Location</th>
                  <th onClick={() => handleSort('mrp')}>MRP</th>
                  <th onClick={() => handleSort('packaging_detail')}>Packaging</th>
                  <th onClick={() => handleSort('medicine_type')}>Medicine Type</th>
                  <th onClick={() => handleSort('last_updated')}>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {inventoryData.map((item) => (
                  <tr key={item.id}>
                    <td>{item.product_id || '-'}</td>
                    <td>{item.name || '-'}</td>
                    <td>{item.manufacturers || '-'}</td>
                    <td>{item.batch_number || '-'}</td>
                    <td>{formatDate(item.expiry_date)}</td>
                    <td>{formatQuantity(item.quantity_available)}</td>
                    <td>
                      <span className={`status-badge status-${item.status?.toLowerCase()}`}>
                        {item.status || '-'}
                      </span>
                    </td>
                    <td>{formatArray(item.rack_location)}</td>
                    <td>{formatCurrency(item.mrp)}</td>
                    <td>{item.packaging_detail || '-'}</td>
                    <td>{item.medicine_type || '-'}</td>
                    <td>{formatDate(item.last_updated)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Inventory; 